// components/features/payments/PaymentRow.tsx
import { Payment } from "../../../types/Payment";
import TableRow from "../../ui/TableRow";
import { CreditCard, User, Mail, Phone, Calendar } from "lucide-react";

interface PaymentRowProps {
  payment: Payment;
  onAction: (payment: Payment) => void;
  onSelect?: (payment: Payment, isSelected: boolean) => void;
  isSelected?: boolean;
  selectable?: boolean;
}

export default function PaymentRow({
  payment,
  onAction,
  onSelect,
  isSelected = false,
  selectable = false
}: PaymentRowProps) {
  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; className: string }> = {
      pending: { label: "En attente", className: "bg-yellow-100 text-yellow-700" },
      approved: { label: "Approuvé", className: "bg-green-100 text-green-700" },
      canceled: { label: "Annulé", className: "bg-red-100 text-red-700" }
    };
    const statusInfo = statusMap[status] || { label: status, className: "bg-gray-100 text-gray-700" };
    return <span className={`px-2 py-1 text-xs rounded-full ${statusInfo.className}`}>{statusInfo.label}</span>;
  };

  const formatDate = (stringDate: string | null) => {
        if (!stringDate) return "-";

    const date = new Date(stringDate)

    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <TableRow
      item={payment}
      onAction={onAction}
      onSelect={onSelect}
      isSelected={isSelected}
      selectable={selectable}
      actionable={true}
    >
      {/* Colonne Référence */}
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <CreditCard size={16} className="text-gray-400" />
          <span className="font-mono text-sm font-medium text-gray-800">{payment.reference}</span>
        </div>
      </td>

      {/* Colonne Client - cachée sur mobile */}
      <td className="hidden sm:table-cell py-3 px-4">
        <div className="flex items-center gap-2">
          <User size={14} className="text-gray-400" />
          <span className="text-gray-700">{payment.customerName}</span>
        </div>
      </td>

      {/* Colonne Email - cachée sur tablette */}
      <td className="hidden md:table-cell py-3 px-4">
        <div className="flex items-center gap-2">
          <Mail size={14} className="text-gray-400" />
          <span className="text-gray-600 text-sm">{payment.customerEmail}</span>
        </div>
      </td>

      {/* Colonne Téléphone - cachée sur tablette */}
      <td className="hidden lg:table-cell py-3 px-4">
        <div className="flex items-center gap-2">
          <Phone size={14} className="text-gray-400" />
          <span className="text-gray-600 text-sm">{payment.customerPhone}</span>
        </div>
      </td>

      {/* Colonne Montant */}
      <td className="py-3 px-4">
        <span className="font-semibold text-gray-900">
          {payment.amount.toLocaleString('fr-FR')} {payment.currency}
        </span>
      </td>

    

      {/* Colonne Statut */}
      <td className="py-3 px-4">
        {getStatusBadge(payment.status)}
      </td>

      {/* Colonne Transaction ID - cachée sur grand écran */}
      <td className="hidden xl:table-cell py-3 px-4">
        <span className="font-mono text-xs text-gray-500">{payment.transactionId}</span>
      </td>

      {/* Colonne Date - cachée sur tablette */}
      <td className="hidden lg:table-cell py-3 px-4">
        <div className="flex items-center gap-2">
          <Calendar size={14} className="text-gray-400" />
          <span className="text-gray-500 text-sm">
            {formatDate(payment.paidAt || payment.createdAt)}</span>
        </div>
      </td>
    </TableRow>
  );
}